// frontend/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { getLanguage } from '../i18n';
import { getReferral, clearReferral } from '../utils/referral';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('glamgo_token');
    if (!token) { setLoading(false); return; }
    // skipAuthRedirect: this call already handles a dead token by clearing it
    // below. Letting the interceptor redirect instead sent anyone with a stale
    // token straight to /login from /delete-account — a page Google Play
    // requires to be readable while signed out.
    api.get('/auth/me', { skipAuthRedirect: true })
      .then(data => setUser(data.user))
      .catch(() => {
        localStorage.removeItem('glamgo_token');
        localStorage.removeItem('glamgo_user');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    localStorage.setItem('glamgo_token', data.token);
    localStorage.setItem('glamgo_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  // consents carries accept_terms / confirm_age. The server refuses without
  // both, and refuses anything that is not literally true — an acceptance the
  // customer did not actively make is not an acceptance.
  const register = async (email, password, full_name, phone, consents = {}) => {
    const data = await api.post('/auth/register', {
      email, password, full_name, phone,
      // The language the signup form was displayed in is the language the Terms
      // were read in, so it is the language every email about this contract has
      // to be written in. Every template used to be Greek only.
      //
      // Read from i18next rather than from localStorage: what was on screen is
      // what was consented to, and the two can differ — localStorage writes are
      // swallowed in Safari private browsing, where the toggle still changes the
      // page but never lands in storage.
      locale: getLanguage(),
      // Who sent them, if anyone. Read here rather than passed in by the form,
      // because the referral was stored when they ARRIVED — possibly on another
      // page, days ago — and no signup form has any way to know about it.
      //
      // Sent unconditionally as undefined when there is none: the server treats
      // a missing code as "no referral" and an unknown one the same way, so this
      // can never fail a registration.
      ref_code: getReferral() || undefined,
      ...consents,
    });
    localStorage.setItem('glamgo_token', data.token);
    localStorage.setItem('glamgo_user', JSON.stringify(data.user));
    // Spent. The server has bound the attribution permanently to this account,
    // and leaving the code behind would mean the next person to register on a
    // shared device is credited to the same partner.
    clearReferral();
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('glamgo_token');
    localStorage.removeItem('glamgo_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
